import React from 'react';

import { Box, Button, CircularProgress, List, ListItem, Typography } from '@mui/material';

import { Modal } from 'src/components/common';
import { useScreenInfo } from 'src/hooks/common';
import { useModalV2 } from 'src/hooks/shared';
import { ModalV2Props } from 'src/types/modalV2';
import { SxProp } from 'src/types/mui';

export type DeleteConfirmationModalProps = {
  /** Shown in the blocked-state title and description, e.g. "PEO staff". */
  entityName: string;
  /** Body text when deletion is allowed. */
  description: string;
  /** Result of the `checkDeletion*` query; undefined while loading or on error. */
  check?: { valid: boolean; errors: string[] } | null;
  loading: boolean;
  deleteLoading: boolean;
  onConfirm: () => Promise<unknown>;
  onDeleted?: () => void;
};

export const DeleteConfirmationModal: React.FC<ModalV2Props<DeleteConfirmationModalProps>> = ({
  id,
  entityName,
  description,
  check,
  loading,
  deleteLoading,
  onConfirm,
  onDeleted,
}) => {
  const { closeModal, switchClosability } = useModalV2();
  const { isMobile } = useScreenInfo();

  const handleCloseModal = React.useCallback(() => {
    closeModal(id);
  }, [closeModal, id]);

  const handleSubmit = async () => {
    try {
      switchClosability(false);
      await onConfirm();
      switchClosability(true);
      closeModal(id);
      onDeleted?.();
    } catch {
      switchClosability(true);
    }
  };

  const isValid = check?.valid;

  const title = isValid ? 'Are you sure?' : `Selected ${entityName} can’t be deleted`;

  return (
    <Modal
      title={loading ? '' : title}
      headerPadding="32px 32px 0 32px"
      dialogProps={{
        onClose: handleCloseModal,
        open: true,
      }}
      maxWidth={540}
    >
      {loading ? (
        <Box sx={loaderStyles}>
          <CircularProgress size={100} color="secondary" />
        </Box>
      ) : (
        <React.Fragment>
          <Typography variant="body1" color="text.secondary">
            {isValid
              ? description
              : `Selected ${entityName} can’t be deleted due to connections that make this operation restricted.`}
          </Typography>

          {isValid === false && (
            <List sx={{ listStyleType: 'disc', ml: 3, mt: 1, p: 0, color: 'text.secondary' }}>
              {check?.errors.map(error => (
                <ListItem sx={{ display: 'list-item', p: 0 }} key={error}>
                  <Typography
                    whiteSpace="pre-line"
                    fontSize="16px"
                    variant="body2"
                    color="text.secondary"
                  >
                    {error}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={isMobile ? 2 : 5}
            sx={{ mt: 4 }}
          >
            {!isValid ? (
              <Button variant="contained" onClick={handleCloseModal}>
                Ok
              </Button>
            ) : (
              <React.Fragment>
                <Button
                  sx={{ width: '150px' }}
                  variant="outlined"
                  color="inherit"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  sx={{ width: '200px' }}
                  onClick={handleSubmit}
                  loading={deleteLoading}
                  variant="contained"
                  color="error"
                >
                  Yes, Delete
                </Button>
              </React.Fragment>
            )}
          </Box>
        </React.Fragment>
      )}
    </Modal>
  );
};

const loaderStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexFlow: 'column',
  height: '300px',
  p: 5,
};
